import React, { useState, useEffect } from "react";

import {
  Plus,
  Eye,
  Edit2,
  Trash2,
  Search,
  X,
  AlertCircle,
  User,
  Calendar,
  Phone,
  BookOpen,
  MessageSquare,
  Users,
  CheckCircle,
  HelpCircle,
  TrendingUp,
  UserCheck,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import "./Enquiry.css";
import { fetchCourseList, fetchBranchList, fetchBatchList, fetchBranchCourseList } from "../../services/masterService";
import { enquiryService } from "../../services/enquiryService";
import StudentFormModal from "../../components/StudentFormModal";

const Enquiry = () => {


  // Data & Loading States
  const [enquiries, setEnquiries] = useState([]);
  const [courses, setCourses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchCourseList, setBranchCourseList] = useState([]);
  const [batches, setBatches] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("All"); // 'All' | 'Pending' | 'Yes' | 'No'
  const [isLoading, setIsLoading] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);

  // Modal States
  const [showAddModal, setShowAddModal] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMappingDropdown, setShowMappingDropdown] = useState(false);

  // Student Form Modal States
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [studentPrefillData, setStudentPrefillData] = useState(null);
  const [enquiryIdToConvert, setEnquiryIdToConvert] = useState(null);
  const [enquiryPayloadToConvert, setEnquiryPayloadToConvert] = useState(null);

  // Form State
  const initialForm = {
    enquirerName: "",
    isStudentEnquirer: false,
    studentName: "",
    relationship: "",
    mobileNumber: "",
    branchCourseId: "",
    enquiryDate: new Date().toISOString().split("T")[0],
    remarks: "",
    followUpDate: "",
    followUpRemarks: "",
    converted: "Pending" // 'Pending' | 'Yes' | 'No'
  };

  const [formData, setFormData] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [popup, setPopup] = useState({ show: false, title: "", message: "", type: "success" });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  // Date Formatter Helper: YYYY-MM-DD -> DD-MM-YYYY
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const parts = dateString.split("T")[0].split("-");
      if (parts.length === 3) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    } catch (err) {
      console.error("Date formatting error:", err);
    }
    return dateString;
  };

  // Load Initial Data
  useEffect(() => {
    loadEnquiries();
    loadCourses();
    loadBranches();
    loadBranchCourseList();
    loadBatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCourses = async () => {
    try {
      const res = await fetchCourseList();
      setCourses(res.data || []);
    } catch (err) {
      console.error("Failed to load courses", err);

    }
  };

  const loadBranches = async () => {
    try {
      const res = await fetchBranchList();
      setBranches(res.data || []);
    } catch (err) {
      console.error("Failed to load branches", err);
    }
  };

  const loadBranchCourseList = async () => {
    try {
      const res = await fetchBranchCourseList();
      setBranchCourseList(res.data || []);
    } catch (err) {
      console.error("Failed to load branch course list", err);
    }
  };

  const loadBatches = async () => {
    try {
      const res = await fetchBatchList();
      setBatches(res.data || []);
    } catch (err) {
      console.error("Failed to load batches", err);
    }
  };

  const loadEnquiries = async () => {
    setIsLoading(true);
    try {
      const res = await enquiryService.getEnquiryList({ page: 1, limit: 10000 });
      
      let apiData = [];
      const d = res.data;
      if (Array.isArray(d)) apiData = d;
      else if (Array.isArray(d?.data)) apiData = d.data;
      else if (Array.isArray(d?.Data)) apiData = d.Data;
      else if (Array.isArray(d?.data?.data)) apiData = d.data.data;
      else if (Array.isArray(d?.data?.Data)) apiData = d.data.Data;
      else if (Array.isArray(d?.items)) apiData = d.items;
      
      const mappedData = apiData.map(raw => {
        const e = {
          id: raw.id || raw.Id,
          enquirerName: raw.enquirerName || raw.EnquirerName || "",
          studentName: raw.studentName || raw.StudentName || "",
          mobileNumber: raw.mobileNumber || raw.MobileNumber || "",
          courseName: raw.courseName || raw.CourseName || "",
          relationship: raw.relationship || raw.Relationship || "",
          enquiryDate: raw.enquiryDate || raw.EnquiryDate || "",
          followUpDate: raw.followUpDate || raw.FollowUpDate || "",
          status: raw.status || raw.Status || "Pending",
          convertedAt: raw.convertedAt || raw.ConvertedAt || null,
          studentId: raw.studentId || raw.StudentId || null
        };

        return {
          ...raw,
          ...e,
          isStudentEnquirer: e.studentName === e.enquirerName && !e.relationship,
          enquiryDate: e.enquiryDate ? e.enquiryDate.split('T')[0] : "",
          followUpDate: e.followUpDate ? e.followUpDate.split('T')[0] : "",
          converted: e.status === "Converted" ? "Yes" : e.status === "Not Converted" ? "No" : "Pending"
        };
      });
      setEnquiries(mappedData);
    } catch (err) {
      console.error("Failed to load enquiries", err);
      showPopup("Error", "Failed to fetch enquiries from server", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const showPopup = (title, message, type = "success") => {
    setPopup({ show: true, title, message, type });
  };

  const handleAddClick = () => {
    setFormData(initialForm);
    setIsEditMode(false);
    setIsViewMode(false);
    setErrors({});
    setShowAddModal(true);
  };

  const handleView = async (item) => {
    setSelectedItem(item);

    let cId = item.courseId || item.CourseId || "";
    let bId = item.branchId || item.BranchId || "";
    let bcId = item.branchCourseId || item.BranchCourseId || "";

    if (!bcId && cId && bId) {
      const group = branchCourseList.find(g => String(g.BranchId || g.id) === String(bId));
      if (group && group.CourseList) {
        const course = group.CourseList.find(c => String(c.CourseId || c.id) === String(cId));
        if (course) {
          bcId = course.BranchCourseId;
        }
      }
    }

    setFormData({ 
      ...item, 
      branchCourseId: bcId
    });
    setIsViewMode(true);
    setIsEditMode(false);
    setErrors({});
    setShowAddModal(true);
    try {
      const res = await enquiryService.getEnquiryDetails(item.id);
      const detail = res.data?.enquiry || res.data;
      if (detail) {
        let dcId = detail.courseId || detail.CourseId || cId;
        let dbId = detail.branchId || detail.BranchId || bId;
        let dbcId = detail.branchCourseId || detail.BranchCourseId || bcId;

        if (!dbcId && dcId && dbId) {
          const group = branchCourseList.find(g => String(g.BranchId || g.id) === String(dbId));
          if (group && group.CourseList) {
            const course = group.CourseList.find(c => String(c.CourseId || c.id) === String(dcId));
            if (course) {
              dbcId = course.BranchCourseId;
            }
          }
        }

        setFormData(prev => ({
          ...prev,
          ...detail,
          enquirerName: detail.enquirerName || detail.EnquirerName || prev.enquirerName,
          studentName: detail.studentName || detail.StudentName || prev.studentName,
          mobileNumber: detail.mobileNumber || detail.MobileNumber || prev.mobileNumber,
          relationship: detail.relationship || detail.Relationship || prev.relationship,
          enquiryDate: detail.enquiryDate ? detail.enquiryDate.split('T')[0] : prev.enquiryDate,
          followUpDate: detail.followUpDate ? detail.followUpDate.split('T')[0] : prev.followUpDate,
          converted: detail.status === "Converted" ? "Yes" : detail.status === "Not Converted" ? "No" : prev.converted,
          remarks: detail.remarks || prev.remarks,
          followUpRemarks: detail.followUpRemarks || prev.followUpRemarks,
          branchCourseId: dbcId
        }));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleEditClick = async (item) => {
    setSelectedItem(item);

    let cId = item.courseId || item.CourseId || "";
    let bId = item.branchId || item.BranchId || "";
    let bcId = item.branchCourseId || item.BranchCourseId || "";

    if (!bcId && cId && bId) {
      const group = branchCourseList.find(g => String(g.BranchId || g.id) === String(bId));
      if (group && group.CourseList) {
        const course = group.CourseList.find(c => String(c.CourseId || c.id) === String(cId));
        if (course) {
          bcId = course.BranchCourseId;
        }
      }
    }

    setFormData({ 
      ...item, 
      branchCourseId: bcId
    });
    setIsEditMode(true);
    setIsViewMode(false);
    setErrors({});
    setShowAddModal(true);
    try {
      const res = await enquiryService.getEnquiryDetails(item.id);
      const detail = res.data?.enquiry || res.data;
      if (detail) {
        let dcId = detail.courseId || detail.CourseId || cId;
        let dbId = detail.branchId || detail.BranchId || bId;
        let dbcId = detail.branchCourseId || detail.BranchCourseId || bcId;

        if (!dbcId && dcId && dbId) {
          const group = branchCourseList.find(g => String(g.BranchId || g.id) === String(dbId));
          if (group && group.CourseList) {
            const course = group.CourseList.find(c => String(c.CourseId || c.id) === String(dcId));
            if (course) {
              dbcId = course.BranchCourseId;
            }
          }
        }

        setFormData(prev => ({
          ...prev,
          ...detail,
          enquirerName: detail.enquirerName || detail.EnquirerName || prev.enquirerName,
          studentName: detail.studentName || detail.StudentName || prev.studentName,
          mobileNumber: detail.mobileNumber || detail.MobileNumber || prev.mobileNumber,
          relationship: detail.relationship || detail.Relationship || prev.relationship,
          enquiryDate: detail.enquiryDate ? detail.enquiryDate.split('T')[0] : prev.enquiryDate,
          followUpDate: detail.followUpDate ? detail.followUpDate.split('T')[0] : prev.followUpDate,
          converted: detail.status === "Converted" ? "Yes" : detail.status === "Not Converted" ? "No" : prev.converted,
          remarks: detail.remarks || prev.remarks,
          followUpRemarks: detail.followUpRemarks || prev.followUpRemarks,
          branchCourseId: dbcId
        }));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteClick = (item) => {
    setSelectedItem(item);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await enquiryService.deleteEnquiry(selectedItem.id);
      const updated = enquiries.filter(e => e.id !== selectedItem.id);
      setEnquiries(updated);
      showPopup("Deleted", "Enquiry record has been removed successfully.");
    } catch (err) {
      showPopup("Error", "Failed to delete enquiry", "error");
    } finally {
      setShowDeleteModal(false);
    }
  };

  // Checkbox sync & toggle logic
  const handleCheckboxChange = (checked) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        isStudentEnquirer: checked,
        relationship: checked ? "" : prev.relationship
      };
      if (checked) {
        updated.studentName = prev.enquirerName;
      }
      return updated;
    });

    // Clear relationship error if checked
    if (checked) {
      setErrors(prev => ({ ...prev, relationship: "", studentName: "" }));
    }
  };

  const handleEnquirerNameChange = (val) => {
    setFormData(prev => {
      const updated = { ...prev, enquirerName: val };
      if (prev.isStudentEnquirer) {
        updated.studentName = val;
      }
      return updated;
    });
    setErrors(prev => ({ ...prev, enquirerName: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    const nameRegex = /^[a-zA-Z\s.]{2,50}$/;
    const phoneRegex = /^\d{10}$/;

    if (!formData.enquirerName?.trim()) {
      newErrors.enquirerName = "Enquirer Name is required";
    } else if (!nameRegex.test(formData.enquirerName)) {
      newErrors.enquirerName = "Letters and spaces only (2-50 chars)";
    }

    if (!formData.studentName?.trim()) {
      newErrors.studentName = "Student Name is required";
    } else if (!nameRegex.test(formData.studentName)) {
      newErrors.studentName = "Letters and spaces only (2-50 chars)";
    }

    if (!formData.isStudentEnquirer && !formData.relationship) {
      newErrors.relationship = "Relationship with student is required";
    }

    if (!formData.mobileNumber) {
      newErrors.mobileNumber = "Mobile Number is required";
    } else if (!phoneRegex.test(formData.mobileNumber)) {
      newErrors.mobileNumber = "Must be exactly 10 digits";
    }

    if (!formData.branchCourseId) {
      newErrors.branchCourseId = "Branch & Course mapping is required";
    }

    if (!formData.enquiryDate) {
      newErrors.enquiryDate = "Enquiry Date is required";
    }

    if (!formData.followUpDate) {
      newErrors.followUpDate = "Follow-up Date is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showPopup("Validation Error", "Please fill all mandatory fields correctly", "error");
      return;
    }

    let selectedCourseId = null;
    let selectedCourseName = "";
    let selectedBranchId = null;
    let selectedBranchName = "";

    const group = branchCourseList.find(g => 
      g.CourseList && g.CourseList.some(c => String(c.BranchCourseId) === String(formData.branchCourseId))
    );
    if (group) {
      selectedBranchId = group.BranchId || group.id;
      selectedBranchName = group.BranchName || group.name || "";
      const course = group.CourseList.find(c => String(c.BranchCourseId) === String(formData.branchCourseId));
      if (course) {
        selectedCourseId = course.CourseId || course.id;
        selectedCourseName = course.CourseName || course.name || "";
      }
    }

    const payload = {
      enquirerName: formData.enquirerName,
      isStudentEnquirer: formData.isStudentEnquirer || false,
      studentName: formData.studentName,
      relationship: formData.relationship || "",
      mobileNumber: formData.mobileNumber,
      courseId: selectedCourseId ? Number(selectedCourseId) : null,
      courseName: selectedCourseName,
      branchId: selectedBranchId ? Number(selectedBranchId) : null,
      branchName: selectedBranchName,
      batchId: null,
      batchName: "",
      branchCourseId: formData.branchCourseId ? Number(formData.branchCourseId) : null,
      enquiryDate: formData.enquiryDate ? `${formData.enquiryDate}T00:00:00` : "",
      remarks: formData.remarks || "",
      followUpDate: formData.followUpDate ? `${formData.followUpDate}T00:00:00` : "",
      followUpRemarks: formData.followUpRemarks || "",
      status: formData.converted === "Yes" ? "Converted" : formData.converted === "No" ? "Not Converted" : "Pending"
    };

    if (formData.converted === "Yes") {
      const prefillData = {
        name: formData.studentName || formData.enquirerName,
        phoneNumber: formData.mobileNumber,
        Parentname: formData.isStudentEnquirer ? "" : formData.enquirerName,
        branchCourseId: formData.branchCourseId || "",
        batchId: "",
        joiningDate: formData.enquiryDate ? formData.enquiryDate.split('T')[0] : new Date().toISOString().split("T")[0],
        email: ""
      };

      setEnquiryPayloadToConvert(payload);
      setEnquiryIdToConvert(isEditMode ? selectedItem.id : null);
      setStudentPrefillData(prefillData);
      setShowAddModal(false);
      setShowStudentModal(true);
      return;
    }

    try {
      if (isEditMode) {
        await enquiryService.updateEnquiry(selectedItem.id, payload);
        showPopup("Success", "Enquiry updated successfully!");
      } else {
        await enquiryService.createEnquiry(payload);
        showPopup("Success", "Enquiry recorded successfully!");
      }

      loadEnquiries();
      setShowAddModal(false);
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to save enquiry";
      showPopup("Error", msg, "error");
    }
  };

  const handleConvertToStudent = async (enquiry) => {
    try {
      setIsLoading(true);
      const res = await enquiryService.getEnquiryDetails(enquiry.id);
      const detail = res.data?.enquiry || res.data;
      
      let prefillData = {
        name: enquiry.studentName,
        phoneNumber: enquiry.mobileNumber,
        Parentname: enquiry.isStudentEnquirer ? "" : enquiry.enquirerName
      };

      if (detail) {
        let cId = detail.courseId || detail.CourseId || enquiry.courseId || enquiry.CourseId || "";
        let bId = detail.branchId || detail.BranchId || enquiry.branchId || enquiry.BranchId || "";
        let bcId = detail.branchCourseId || detail.BranchCourseId || enquiry.branchCourseId || enquiry.BranchCourseId;

        if (!bcId && cId && bId) {
          const group = branchCourseList.find(g => String(g.BranchId || g.id) === String(bId));
          if (group && group.CourseList) {
            const course = group.CourseList.find(c => String(c.CourseId || c.id) === String(cId));
            if (course) {
              bcId = course.BranchCourseId;
            }
          }
        }
        
        if (bcId && (!cId || !bId)) {
          const group = branchCourseList.find(g => 
            g.CourseList && g.CourseList.some(c => String(c.BranchCourseId) === String(bcId))
          );
          if (group) {
            bId = group.BranchId || group.id;
            const course = group.CourseList.find(c => String(c.BranchCourseId) === String(bcId));
            if (course) {
              cId = course.CourseId || course.id;
            }
          }
        }

        const payload = {
          ...detail,
          courseId: cId,
          branchId: bId,
          batchId: null,
          branchCourseId: bcId,
          status: "Converted"
        };

        prefillData = {
          name: detail.studentName || enquiry.studentName,
          phoneNumber: detail.mobileNumber || enquiry.mobileNumber,
          Parentname: (detail.isStudentEnquirer || enquiry.isStudentEnquirer) ? "" : (detail.enquirerName || enquiry.enquirerName),
          branchCourseId: bcId || "",
          batchId: "",
          joiningDate: detail.enquiryDate ? detail.enquiryDate.split('T')[0] : new Date().toISOString().split("T")[0],
          email: detail.email || enquiry.email || ""
        };

        setEnquiryPayloadToConvert(payload);
        setEnquiryIdToConvert(enquiry.id);
        setStudentPrefillData(prefillData);
        setShowStudentModal(true);
      }
    } catch (err) {
      console.error(err);
      showPopup("Error", "Failed to fetch enquiry details for conversion", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Search & Filters
  const filteredEnquiries = enquiries.filter(e => {
    const matchesSearch =
      (e.enquirerName?.toLowerCase()?.includes(searchTerm.toLowerCase()) || false) ||
      (e.studentName?.toLowerCase()?.includes(searchTerm.toLowerCase()) || false) ||
      (e.mobileNumber?.includes(searchTerm) || false) ||
      (e.courseName?.toLowerCase()?.includes(searchTerm.toLowerCase()) || false);

    if (activeFilter === "All") return matchesSearch;
    return matchesSearch && e.converted === activeFilter;
  });

  // Pagination Details
  const totalPages = Math.ceil(filteredEnquiries.length / itemsPerPage);
  const paginatedEnquiries = filteredEnquiries.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeFilter]);

  // Analytics Stats
  const totalEnquiries = enquiries.length;
  const pendingCount = enquiries.filter(e => e.converted === "Pending").length;
  const convertedCount = enquiries.filter(e => e.converted === "Yes").length;
  const notConvertedCount = enquiries.filter(e => e.converted === "No").length;

  const checkIfDirty = () => {
    if (!showAddModal || isViewMode) return false;
    const initial = isEditMode ? selectedItem : initialForm;
    return JSON.stringify(formData) !== JSON.stringify(initial);
  };

  const handleCloseModal = () => {
    if (checkIfDirty()) {
      setShowDiscardModal(true);
    } else {
      setShowAddModal(false);
    }
  };

  const confirmDiscard = () => {
    setShowDiscardModal(false);
    setShowAddModal(false);
  };

  return (
    <div className="enquiry-container">
      {/* 🚀 ANALYTICS SUMMARY CARDS */}
      {showAnalytics && (
        <div className="enquiry-analytics-row">
          <div className="analytic-card">
          <div className="analytic-icon icon-blue"><Users size={22} /></div>
          <div className="analytic-details">
            <span className="analytic-lbl">Total Enquiries</span>
            <span className="analytic-val">{totalEnquiries}</span>
          </div>
        </div>
        <div className="analytic-card">
          <div className="analytic-icon icon-orange"><HelpCircle size={22} /></div>
          <div className="analytic-details">
            <span className="analytic-lbl">Pending Follow-ups</span>
            <span className="analytic-val">{pendingCount}</span>
          </div>
        </div>
        <div className="analytic-card">
          <div className="analytic-icon icon-green"><UserCheck size={22} /></div>
          <div className="analytic-details">
            <span className="analytic-lbl">Converted to Student</span>
            <span className="analytic-val">{convertedCount}</span>
          </div>
        </div>
        <div className="analytic-card">
          <div className="analytic-icon icon-red"><TrendingUp size={22} style={{ transform: "rotate(90deg)" }} /></div>
          <div className="analytic-details">
            <span className="analytic-lbl">Not Converted</span>
            <span className="analytic-val">{notConvertedCount}</span>
          </div>
        </div>
      </div>
      )}

      {/* 🚀 HEADER CONTROLS */}
      <div className="enquiry-header">
        <div className="header-left">
          <button className="analytics-toggle-btn" onClick={() => setShowAnalytics(!showAnalytics)} title="Toggle Analytics">
            <TrendingUp size={16} />
            <span className="hide-mobile">Analytics</span>
            {showAnalytics ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          <div className="filter-badge-selector">
            <button className={`filter-badge-btn ${activeFilter === "All" ? "active" : ""}`} onClick={() => setActiveFilter("All")}>All</button>
            <button className={`filter-badge-btn ${activeFilter === "Pending" ? "active" : ""}`} onClick={() => setActiveFilter("Pending")}>Pending</button>
            <button className={`filter-badge-btn ${activeFilter === "Yes" ? "active" : ""}`} onClick={() => setActiveFilter("Yes")}>Converted</button>
            <button className={`filter-badge-btn ${activeFilter === "No" ? "active" : ""}`} onClick={() => setActiveFilter("No")}>Not Converted</button>
          </div>

          <div className="search-box">
            <Search size={18} className="filter-icon" />
            <input
              type="text"
              placeholder="Search by name, mobile, course..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="header-right">
          <button className="add-enquiry-btn" onClick={handleAddClick}>
            <Plus size={18} />
            <span>Add Enquiry</span>
          </button>
        </div>
      </div>

      {/* 🚀 TABLE CONTAINER */}
      <div className="enquiry-grid-wrapper">
        {isLoading && (
          <div className="loader-overlay">
            <div className="loader-spinner"></div>
            <p>Fetching Data...</p>
          </div>
        )}

        <div className="table-scroll-area">
          <table className="enquiry-table">
            <thead>
              <tr>
                <th className="text-center" style={{ width: "80px" }}>S.No</th>
                <th>Enquirer Name</th>
                <th>Student Name</th>
                <th>Relationship</th>
                <th className="text-center">Mobile Number</th>
                <th className="text-center">Course Name</th>
                <th className="text-center">Enquiry Date</th>
                <th className="text-center">Follow-up Date</th>
                <th className="text-center">Status</th>
                <th className="text-center" style={{ width: "180px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedEnquiries.length > 0 ? (
                paginatedEnquiries.map((e, index) => (
                  <tr key={e.id}>
                    <td className="text-center"><span className="id-badge">{(currentPage - 1) * itemsPerPage + index + 1}</span></td>
                    <td style={{ fontWeight: 600 }}>{e.enquirerName}</td>
                    <td>{e.studentName}</td>
                    <td>{e.relationship || "Self"}</td>
                    <td className="mobile-txt text-center">{e.mobileNumber}</td>
                    <td className="text-center"><span className="course-badge">{e.courseName}</span></td>
                    <td className="text-center">{formatDateForDisplay(e.enquiryDate)}</td>
                    <td className="text-center">{formatDateForDisplay(e.followUpDate)}</td>
                    <td className="text-center">
                      <span className={`status-badge ${e.converted === "Yes" ? "converted" : e.converted === "No" ? "notconverted" : "pending"}`}>
                        {e.converted === "Yes" ? "Converted" : e.converted === "No" ? "Not Converted" : "Pending"}
                      </span>
                    </td>
                    <td className="text-center">
                      <div className="action-cell">
                        <button className="action-btn view" onClick={() => handleView(e)} title="View Enquiry">
                          <Eye size={16} />
                        </button>
                        <button className="action-btn edit" onClick={() => handleEditClick(e)} title="Edit Enquiry">
                          <Edit2 size={16} />
                        </button>
                        {e.converted === "Yes" ? (
                          <button className="action-btn convert-disabled" disabled title="Already Onboarded as Student">
                            <CheckCircle size={16} style={{ color: "#16a34a" }} />
                          </button>
                        ) : (
                          <button className="action-btn convert" onClick={() => handleConvertToStudent(e)} title="Onboard Student">
                            <CheckCircle size={16} />
                          </button>
                        )}
                        <button className="action-btn delete" onClick={() => handleDeleteClick(e)} title="Delete">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="text-center" style={{ padding: "40px", color: "#94a3b8" }}>
                    No enquiries found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* 🚀 PAGINATION CONTROLS */}
        {totalPages > 1 && (
          <div className="pagination-container">
            <div className="pagination-info">
              Showing <span>{(currentPage - 1) * itemsPerPage + 1}</span> to <span>{Math.min(currentPage * itemsPerPage, filteredEnquiries.length)}</span> of <span>{filteredEnquiries.length}</span> enquiries
            </div>
            <div className="pagination-btns">
              <button
                className="pag-btn"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i + 1}
                  className={`pag-num ${currentPage === i + 1 ? 'active' : ''}`}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              <button
                className="pag-btn"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 🚀 ADD / EDIT MODAL */}
      {showAddModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-card wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{isViewMode ? "View Enquiry Details" : isEditMode ? "Edit Enquiry Details" : "Record New Enquiry"}</h3>
              <button className="close-btn" onClick={handleCloseModal}><X size={18} /></button>
            </div>
            <div className="modal-body scrollable">
              <div className="enquiry-form-layout">
                {/* Basic Section */}
                <div className="form-section">
                  <h4><User size={15} /> Primary Contact Information</h4>
                  <div className="form-grid-inner">
                    <div className={`form-input-group ${errors.enquirerName ? "has-error" : ""}`}>
                      <label>Enquirer Name *</label>
                      <div className={`input-with-icon ${isViewMode ? "readonly-input-wrap" : ""}`}>
                        <span className="input-icon"><User size={15} /></span>
                        <input
                          type="text"
                          value={formData.enquirerName}
                          onChange={(e) => {
                            if (isViewMode) return;
                            handleEnquirerNameChange(e.target.value.replace(/[0-9]/g, ""));
                          }}
                          placeholder={isViewMode ? "" : "Enter Enquirer Name"}
                          readOnly={isViewMode}
                        />
                      </div>
                      {errors.enquirerName && <span className="error-message-inline">{errors.enquirerName}</span>}
                    </div>

                    <div className={`form-input-group ${errors.mobileNumber ? "has-error" : ""}`}>
                      <label>Mobile Number *</label>
                      <div className={`input-with-icon ${isViewMode ? "readonly-input-wrap" : ""}`}>
                        <span className="input-icon"><Phone size={15} /></span>
                        <input
                          type="text"
                          maxLength={10}
                          value={formData.mobileNumber}
                          onChange={(e) => {
                            if (isViewMode) return;
                            setFormData({ ...formData, mobileNumber: e.target.value.replace(/\D/g, "") });
                            setErrors({ ...errors, mobileNumber: "" });
                          }}
                          placeholder={isViewMode ? "" : "Enter 10-Digit Mobile"}
                          readOnly={isViewMode}
                        />
                      </div>
                      {errors.mobileNumber && <span className="error-message-inline">{errors.mobileNumber}</span>}
                    </div>

                    <div className={`form-input-group ${errors.branchCourseId ? 'has-error' : ''}`} style={{ gridColumn: "1 / -1", maxWidth: "440px" }}>
                      <label>Interested Course & Branch *</label>
                      <div
                        className={`custom-select-wrapper ${isViewMode ? 'disabled' : ''} ${showMappingDropdown ? 'open' : ''} ${errors.branchCourseId ? 'error-border' : ''}`}
                        onClick={() => !isViewMode && setShowMappingDropdown(!showMappingDropdown)}
                      >
                        <div className="selected-value">
                          {formData.branchCourseId ? (
                            branchCourseList.find(group => group.CourseList && group.CourseList.some(c => String(c.BranchCourseId) === String(formData.branchCourseId)))?.CourseList.find(c => String(c.BranchCourseId) === String(formData.branchCourseId)) ? (
                              `${branchCourseList.find(group => group.CourseList && group.CourseList.some(c => String(c.BranchCourseId) === String(formData.branchCourseId)))?.CourseList.find(c => String(c.BranchCourseId) === String(formData.branchCourseId))?.CourseName} - ${branchCourseList.find(group => group.CourseList && group.CourseList.some(c => String(c.BranchCourseId) === String(formData.branchCourseId)))?.BranchName}`
                            ) : "Select Course - Branch"
                          ) : "Select Course - Branch"}
                        </div>
                        <ChevronDown size={18} className="select-arrow" />
                        
                        {showMappingDropdown && (
                          <div className="custom-dropdown-options" style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 10 }}>
                            <div
                              className="custom-option"
                              onClick={(e) => {
                                e.stopPropagation();
                                setFormData({ ...formData, branchCourseId: "" });
                                setErrors(prev => ({ ...prev, branchCourseId: "" }));
                                setShowMappingDropdown(false);
                              }}
                            >
                              Select Course - Branch
                            </div>
                            {branchCourseList.map((group, gIdx) => (
                              <div key={`branch-${group.BranchId || group.id}-${gIdx}`} className="option-group">
                                <div className="group-label">{group.BranchName || group.name}</div>
                                {group.CourseList && group.CourseList.map((course, cIdx) => (
                                  <div
                                    key={`course-${course.BranchCourseId}-${cIdx}`}
                                    className={`custom-option ${String(formData.branchCourseId) === String(course.BranchCourseId) ? 'active' : ''}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setFormData({ ...formData, branchCourseId: course.BranchCourseId });
                                      setErrors(prev => ({ ...prev, branchCourseId: "" }));
                                      setShowMappingDropdown(false);
                                    }}
                                  >
                                    {course.CourseName || course.name}
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {errors.branchCourseId && <span className="error-message-inline">{errors.branchCourseId}</span>}
                    </div>
                  </div>
                </div>

                {/* Student Relationship Section */}
                <div className="form-section">
                  <h4><Users size={15} /> Student & Relationship Details</h4>

                  <div style={{ marginBottom: "16px" }}>
                    <label className={`checkbox-container ${isViewMode ? "readonly-input-wrap" : ""}`}>
                      <input
                        type="checkbox"
                        checked={formData.isStudentEnquirer}
                        onChange={(e) => {
                          if (isViewMode) return;
                          handleCheckboxChange(e.target.checked);
                        }}
                        disabled={isViewMode}
                      />
                      <span className="custom-checkbox"></span>
                      Student is the Enquirer
                    </label>
                  </div>

                  <div className="form-grid-inner">
                    <div className={`form-input-group ${errors.studentName ? "has-error" : ""}`}>
                      <label>Student Name *</label>
                      <div className={`input-with-icon ${(isViewMode || formData.isStudentEnquirer) ? "readonly-input-wrap" : ""}`}>
                        <span className="input-icon"><User size={15} /></span>
                        <input
                          type="text"
                          value={formData.studentName}
                          onChange={(e) => {
                            if (isViewMode || formData.isStudentEnquirer) return;
                            setFormData({ ...formData, studentName: e.target.value.replace(/[0-9]/g, "") });
                            setErrors({ ...errors, studentName: "" });
                          }}
                          placeholder={(isViewMode || formData.isStudentEnquirer) ? "" : "Enter Student Name"}
                          readOnly={isViewMode || formData.isStudentEnquirer}
                        />
                      </div>
                      {errors.studentName && <span className="error-message-inline">{errors.studentName}</span>}
                    </div>

                    <div className={`form-input-group ${errors.relationship ? "has-error" : ""}`}>
                      <label>Relationship with Student *</label>
                      <div className={`input-with-icon ${(isViewMode || formData.isStudentEnquirer) ? "readonly-input-wrap" : ""}`}>
                        <span className="input-icon"><Users size={15} /></span>
                        <select
                          value={formData.relationship}
                          onChange={(e) => {
                            if (isViewMode || formData.isStudentEnquirer) return;
                            setFormData({ ...formData, relationship: e.target.value });
                            setErrors({ ...errors, relationship: "" });
                          }}
                          disabled={isViewMode || formData.isStudentEnquirer}
                          className="form-select-modern"
                        >
                          <option value="">-- Choose Relationship --</option>
                          <option value="Father">Father</option>
                          <option value="Mother">Mother</option>
                          <option value="Guardian">Guardian</option>
                          <option value="Self">Self</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      {errors.relationship && <span className="error-message-inline">{errors.relationship}</span>}
                    </div>
                  </div>
                </div>

                {/* Enquiry Details */}
                <div className="form-section">
                  <h4><MessageSquare size={15} /> Enquiry Logs & Remarks</h4>
                  <div className="form-grid-inner" style={{ gridTemplateColumns: "1fr 1fr" }}>
                    <div className={`form-input-group ${errors.enquiryDate ? "has-error" : ""}`}>
                      <label>Enquiry Date *</label>
                      <div className={`input-with-icon ${isViewMode ? "readonly-input-wrap" : ""}`}>
                        <span className="input-icon"><Calendar size={15} /></span>
                        <input
                          type="date"
                          value={formData.enquiryDate}
                          onChange={(e) => {
                            if (isViewMode) return;
                            setFormData({ ...formData, enquiryDate: e.target.value });
                            setErrors({ ...errors, enquiryDate: "" });
                          }}
                          readOnly={isViewMode}
                        />
                      </div>
                      {errors.enquiryDate && <span className="error-message-inline">{errors.enquiryDate}</span>}
                    </div>

                    <div className={`form-input-group ${errors.followUpDate ? "has-error" : ""}`}>
                      <label>Follow-up Date *</label>
                      <div className={`input-with-icon ${isViewMode ? "readonly-input-wrap" : ""}`}>
                        <span className="input-icon"><Calendar size={15} /></span>
                        <input
                          type="date"
                          value={formData.followUpDate}
                          onChange={(e) => {
                            if (isViewMode) return;
                            setFormData({ ...formData, followUpDate: e.target.value });
                            setErrors({ ...errors, followUpDate: "" });
                          }}
                          readOnly={isViewMode}
                        />
                      </div>
                      {errors.followUpDate && <span className="error-message-inline">{errors.followUpDate}</span>}
                    </div>

                    <div className="form-input-group" style={{ gridColumn: "1 / -1" }}>
                      <label>Remarks</label>
                      <div className={`input-with-icon ${isViewMode ? "readonly-input-wrap" : ""}`}>
                        <span className="input-icon" style={{ top: "20px" }}><MessageSquare size={15} /></span>
                        <textarea
                          value={formData.remarks}
                          onChange={(e) => {
                            if (isViewMode) return;
                            setFormData({ ...formData, remarks: e.target.value });
                          }}
                          placeholder={isViewMode ? "" : "Enter initial enquiry comments..."}
                          readOnly={isViewMode}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Follow-up Section (only in Edit/View or if converting) */}
                {(isEditMode || isViewMode) && (
                  <div className="form-section" style={{ background: "#f8fafc", padding: "16px", borderRadius: "12px", border: "1px dashed #cbd5e1" }}>
                    <h4 style={{ borderBottomColor: "#cbd5e1" }}><CheckCircle size={15} /> Follow-up Action & Conversions</h4>
                    <div className="form-grid-inner">
                      <div className="form-input-group">
                        <label>Converted to Student?</label>
                        <div className={`input-with-icon ${isViewMode ? "readonly-input-wrap" : ""}`}>
                          <span className="input-icon"><CheckCircle size={15} /></span>
                          <select
                            value={formData.converted}
                            onChange={(e) => {
                              if (isViewMode) return;
                              setFormData({ ...formData, converted: e.target.value });
                            }}
                            disabled={isViewMode}
                            className="form-select-modern"
                          >
                            <option value="Pending">Pending / In-progress</option>
                            <option value="Yes">Yes (Converted)</option>
                            <option value="No">No (Not Converted)</option>
                          </select>
                        </div>
                      </div>

                      <div className="form-input-group" style={{ gridColumn: "1 / -1" }}>
                        <label>Follow-up Remarks / Actions Taken</label>
                        <div className={`input-with-icon ${isViewMode ? "readonly-input-wrap" : ""}`}>
                          <span className="input-icon" style={{ top: "20px" }}><MessageSquare size={15} /></span>
                          <textarea
                            value={formData.followUpRemarks}
                            onChange={(e) => {
                              if (isViewMode) return;
                              setFormData({ ...formData, followUpRemarks: e.target.value });
                            }}
                            placeholder={isViewMode ? "" : "Record follow-up remarks..."}
                            readOnly={isViewMode}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={handleCloseModal}>{isViewMode ? "Close" : "Cancel"}</button>
              {!isViewMode && (
                <button className="save-enquiry-btn" onClick={handleSave}>
                  {isEditMode ? "Save Changes" : "Save Enquiry"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* DISCARD CHANGES CONFIRMATION */}
      {showDiscardModal && (
        <div className="modal-overlay" onClick={() => setShowDiscardModal(false)}>
          <div className="modal-card mini" onClick={(e) => e.stopPropagation()}>
            <div className="delete-confirm-body">
              <AlertCircle size={44} color="#f59e0b" style={{ margin: "0 auto" }} />
              <h3>Discard Unsaved Changes?</h3>
              <p>You have unsaved changes. Are you sure you want to close? All details entered will be removed.</p>
              <div className="confirm-actions">
                <button className="cancel-btn" onClick={() => setShowDiscardModal(false)}>Keep Editing</button>
                <button className="delete-confirm-btn" style={{ background: '#f59e0b' }} onClick={confirmDiscard}>Discard</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DELETE MODAL */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-card mini" onClick={(e) => e.stopPropagation()}>
            <div className="delete-confirm-body">
              <AlertCircle size={44} color="#ef4444" style={{ margin: "0 auto" }} />
              <h3>Confirm Delete</h3>
              <p>Are you sure you want to remove the enquiry for <b>{selectedItem?.enquirerName}</b>? This action is permanent.</p>
              <div className="confirm-actions">
                <button className="cancel-btn" onClick={() => setShowDeleteModal(false)}>Keep Record</button>
                <button className="delete-confirm-btn" onClick={confirmDelete}>Delete Now</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* STUDENT CONVERSION MODAL */}
      <StudentFormModal
        isOpen={showStudentModal}
        onClose={() => {
          setShowStudentModal(false);
          setEnquiryIdToConvert(null);
          setEnquiryPayloadToConvert(null);
        }}
        onSuccess={async (msg) => {
          try {
            if (enquiryPayloadToConvert) {
              if (enquiryIdToConvert) {
                await enquiryService.updateEnquiry(enquiryIdToConvert, enquiryPayloadToConvert);
              } else {
                await enquiryService.createEnquiry(enquiryPayloadToConvert);
              }
            }
            showPopup("Success", "Student Onboarded and Enquiry Converted Successfully!");
            setShowStudentModal(false);
            setEnquiryIdToConvert(null);
            setEnquiryPayloadToConvert(null);
            loadEnquiries();
          } catch(err) {
            showPopup("Error", "Student created, but failed to update Enquiry status.", "error");
          }
        }}
        prefillData={studentPrefillData}
        branchCourseList={branchCourseList}
        batches={batches}
      />

      {/* POPUP NOTIFICATION */}
      {popup.show && (
        <div className="popup-overlay" onClick={() => setPopup({ ...popup, show: false })}>
          <div className="popup-card" onClick={(e) => e.stopPropagation()}>
            <div className={`popup-icon ${popup.type}`}>
              {popup.type === "success" ? "✓" : "!"}
            </div>
            <h2>{popup.title}</h2>
            <p>{popup.message}</p>
            <button className="popup-btn" onClick={() => setPopup({ ...popup, show: false })}>Got it</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Enquiry;
